import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import ContactForm from '../components/ContactForm';
import MediaPlaceholder from '../components/MediaPlaceholder';
import logo from '../assets/logo.jpeg';

// Use relative URL in production for same-origin requests
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? ''
  : (process.env.REACT_APP_API_URL || 'http://localhost:8000');

const LandingPage = () => {
  const [release, setRelease] = useState({
    version: null,
    file_url: null,
    file_size: null,
    file_name: 'KrossIQ-Setup.exe'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLatestRelease = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/releases/latest/`);
        if (response.ok) {
          const data = await response.json();
          // Clean up version format (remove leading V if present)
          if (data.version) {
            data.version = data.version.replace(/^V/i, '');
          }
          setRelease(data);
          setError(null);
        } else {
          console.error('API response not OK:', response.status);
          setError('Failed to load release info');
        }
      } catch (err) {
        console.error('Failed to fetch latest release:', err);
        setError('Failed to load release info');
      } finally {
        setLoading(false);
      }
    };
    fetchLatestRelease();
  }, []);

  const formatFileSize = (bytes) => {
    if (!bytes) return '~500 MB';
    const mb = bytes / (1024 * 1024);
    return `${Math.round(mb)} MB`;
  };

  const handleBookDemo = () => {
    window.dispatchEvent(new CustomEvent('openContactForm', {
      detail: { message: 'I would like to request a demo of KrossIQ. Please contact me to schedule a convenient time.' }
    }));
  };

  const handleContactSales = () => {
    window.dispatchEvent(new CustomEvent('openContactForm', {
      detail: { message: 'I am interested in purchasing KrossIQ for my team. Please provide pricing details and licensing options.' }
    }));
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="nav-container">
          <a href="#top" className="nav-logo" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <img src={logo} alt="KrossIQ — crosstabs and AI-assisted reporting for market research" className="logo-image" />
          </a>
          <div className="nav-links">
            <a href="#features">Features</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#download">Download</a>
            <a href="#pricing">Pricing</a>
            <Link to="/blog">Blog</Link>
          </div>
          <div className="nav-actions">
            <button onClick={handleBookDemo} className="btn btn-outline btn-sm">Book Demo</button>
            <a href="#download" className="btn btn-primary btn-sm">Download</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <span>Built for market research agencies &amp; DP teams</span>
            </div>
            <h1>From Raw Survey Data to Client-Ready Reports in Minutes</h1>
            <p className="hero-description">
              Replace SPSS, Q, and manual PowerPoint workflows with a single automated platform
              for crosstabs, statistical testing, AI-assisted insights, and storytelling.
              Upload your <code>.sav</code> — get a publication-ready deck in one workflow.
            </p>
            <div className="hero-cta">
              <button onClick={handleBookDemo} className="btn btn-primary btn-lg">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                Book a 10-min Demo
              </button>
              <a href="#download" className="btn btn-outline btn-lg">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Try Free for 14 Days
              </a>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-value">70%</span>
                <span className="stat-label">Less Manual Reporting</span>
              </div>
              <div className="stat">
                <span className="stat-value">1-Workflow</span>
                <span className="stat-label">Data to Deck</span>
              </div>
              <div className="stat">
                <span className="stat-value">SPSS .sav</span>
                <span className="stat-label">Native Support</span>
              </div>
            </div>
          </div>
          <div className="hero-image">
            <MediaPlaceholder
              type="video"
              src="/media/hero-demo.mp4"
              poster="/media/hero-poster.jpg"
              suggestedFilename="hero-demo.mp4"
              suggestedSize="1280×720"
              description="60-second product walkthrough — upload, AI tab plan, export"
              aspectRatio="16/9"
              alt="KrossIQ product demo video"
              caption="Drop hero-demo.mp4 (or hero-product-screenshot.png as fallback) into /public/media/"
            />
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="value-prop-section" style={{
        background: 'linear-gradient(135deg, #fef3f2 0%, #fff1f2 50%, #fff7ed 100%)',
        padding: '80px 0',
        borderTop: '1px solid #fee4e2'
      }}>
        <div className="section-container">
          <div className="section-header">
            <div className="section-subtitle" style={{ color: '#dc2626' }}>The Problem</div>
            <h2>Stop Spending Hours on Manual Reporting</h2>
            <p>Most research teams still follow this broken workflow:</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', maxWidth: '960px', margin: '40px auto 0' }}>
            {[
              { step: '1', title: 'Data Processing', tool: 'SPSS', pain: 'Hours of syntax writing, banner setup, weights applied by hand' },
              { step: '2', title: 'Analysis', tool: 'Q Research Software', pain: 'Separate tool, separate learning curve, still manual' },
              { step: '3', title: 'Charts & Reports', tool: 'PowerPoint', pain: 'Copy-paste between Q3 and Q4. Formatting errors. Every. Time.' }
            ].map((item, i) => (
              <div key={i} style={{
                background: 'white',
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid #fecaca',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
              }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: '#fee2e2', color: '#dc2626',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, marginBottom: '12px'
                }}>{item.step}</div>
                <h4 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '4px', color: '#1e293b' }}>{item.title}</h4>
                <p style={{ fontSize: '13px', color: '#dc2626', fontWeight: 600, marginBottom: '8px' }}>{item.tool}</p>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.5, margin: 0 }}>{item.pain}</p>
              </div>
            ))}
          </div>
          <div style={{
            textAlign: 'center', marginTop: '48px', padding: '32px',
            background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
            borderRadius: '16px', border: '1px solid #bbf7d0',
            maxWidth: '800px', margin: '48px auto 0'
          }}>
            <h3 style={{ fontSize: '22px', color: '#059669', marginBottom: '12px' }}>KrossIQ replaces all of it.</h3>
            <p style={{ fontSize: '15px', color: '#065f46', marginBottom: '16px' }}>
              Upload your dataset → get structured crosstabs, statistical testing, automated charts,
              AI-assisted insights, and ready-to-use reports.
              <br/><strong>All in one workflow. No copy-paste.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="benefits-section" style={{ padding: '80px 0', background: '#ffffff' }}>
        <div className="section-container">
          <div className="section-header">
            <div className="section-subtitle">Why Teams Switch</div>
            <h2>Outcomes, Not Features</h2>
            <p>What actually changes when a team adopts KrossIQ</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px', maxWidth: '1100px', margin: '0 auto' }}>
            {[
              { icon: '🚀', title: 'Faster Reporting', desc: 'Turn around tab books in hours instead of days. AI drafts the plan; the engine runs it.' },
              { icon: '💰', title: 'Reduce Analyst Workload', desc: 'Seniors spend time on story and strategy. DP stops copy-pasting between questions.' },
              { icon: '📊', title: 'Consistent, Error-Free', desc: 'Standardised tables, engine-computed numbers, banners reused across waves. No drift.' },
              { icon: '🤖', title: 'Built for Scale', desc: 'Handle multiple concurrent projects without growing headcount. Templates and presets travel.' }
            ].map((b, i) => (
              <div key={i} style={{
                padding: '28px 24px',
                border: '1px solid #e2e8f0',
                borderRadius: '14px',
                background: '#fafbff',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>{b.icon}</div>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>{b.title}</h3>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6, margin: 0 }}>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-container">
          <div className="section-header">
            <div className="section-subtitle">Comprehensive Toolkit</div>
            <h2>Everything You Need for Survey Data Analysis</h2>
            <p>Data computation, preparation, and analysis — all in one powerful tool</p>
          </div>
          <div className="features-grid">
            {[
              {
                icon: 'table',
                title: 'Cross-Tabulation',
                desc: 'Create professional banner tables with statistical significance testing, nets, and multiple response sets in seconds.'
              },
              {
                icon: 'database',
                title: 'SPSS File Support',
                desc: 'Import .sav files with full metadata preservation — variable labels, value labels, missing values, and multiple response sets.'
              },
              {
                icon: 'brain',
                title: 'AI-Powered Assistance',
                desc: 'AI helps with data computation, quick summaries, easy creation of nets, reordering, and intelligent suggestions.'
              },
              {
                icon: 'chart',
                title: 'Statistical Testing',
                desc: 'Chi-square, T-tests, Z-tests, and column proportion tests with automatic significance highlighting (A/B/C notation).'
              },
              {
                icon: 'box',
                title: 'Factor Analysis',
                desc: 'Run exploratory factor analysis with rotation options, scree plots, and factor loadings visualization.'
              },
              {
                icon: 'target',
                title: 'TURF Analysis',
                desc: 'Optimize product portfolios with Total Unduplicated Reach and Frequency analysis. Find the best combination of items.'
              },
              {
                icon: 'maxdiff',
                title: 'MaxDiff Analysis',
                desc: 'Maximum Difference Scaling to measure preference and importance. Design generator, data converter, and score analysis built-in.'
              },
              {
                icon: 'trend',
                title: 'Driver Analysis',
                desc: 'Identify key drivers with SHAP-powered importance analysis. Understand what really impacts your KPIs.'
              },
              {
                icon: 'export',
                title: 'Export to Excel',
                desc: 'One-click export of tables and charts to professionally formatted Excel workbooks with full styling preserved.'
              },
              {
                icon: 'scale',
                title: 'Data Weighting',
                desc: 'Apply rim weighting, cell weighting, or custom weight variables to ensure representative results.'
              },
              {
                icon: 'check',
                title: 'Data QC & Validation',
                desc: 'Automated quality checks for outliers, speeders, straight-liners, and custom validation rules.'
              },
              {
                icon: 'box-stats',
                title: 'Top/Bottom Box',
                desc: 'Instantly calculate top-box, bottom-box, and custom aggregations across scale questions.'
              },
              {
                icon: 'correlation',
                title: 'Correlation Matrix',
                desc: 'Generate correlation matrices with significance indicators and export-ready heatmap visualizations.'
              },
              {
                icon: 'conjoint',
                title: 'CBC Conjoint Analysis',
                desc: 'Choice-Based Conjoint with MNL and Hierarchical Bayes estimation. Design generator, market simulator, and utility score export.'
              },
              {
                icon: 'sig',
                title: 'Significance Testing',
                desc: 'Column proportions tests, complex samples t-tests, and pairwise comparisons with A/B/C letter notation and color highlighting.'
              }
            ].map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">
                  {getFeatureIcon(feature.icon)}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Graceful Expiry Highlight Section */}
      <section style={{
        background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0f9ff 100%)',
        padding: '60px 0',
        borderTop: '1px solid #d1fae5',
        borderBottom: '1px solid #d1fae5'
      }}>
        <div className="section-container">
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '48px',
            maxWidth: '1000px',
            margin: '0 auto',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <div style={{ flex: '0 0 80px', textAlign: 'center' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)'
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: '280px' }}>
              <div style={{
                display: 'inline-block',
                padding: '4px 12px',
                backgroundColor: '#d1fae5',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#059669',
                marginBottom: '12px',
                letterSpacing: '0.05em',
                textTransform: 'uppercase'
              }}>
                Our Promise
              </div>
              <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '12px', lineHeight: '1.3' }}>
                Your Data is Always Yours
              </h2>
              <p style={{ fontSize: '16px', color: '#475569', lineHeight: '1.7', marginBottom: '16px' }}>
                Even if your trial or license expires, you <strong>never lose access</strong> to your existing projects.
                View, analyze, filter, export — everything works. Only new data imports are paused until you renew.
                We believe your research shouldn't be held hostage.
              </p>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                {[
                  { label: 'View & Analyze', desc: 'Full access to existing projects' },
                  { label: 'Export Results', desc: 'Excel, charts — all yours' },
                  { label: 'No Data Loss', desc: 'Your projects stay intact forever' }
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" style={{ flexShrink: 0, marginTop: '2px' }}>
                      <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" />
                    </svg>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>{item.label}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section">
        <div className="section-container">
          <div className="section-header">
            <div className="section-subtitle">Simple Workflow</div>
            <h2>From Data to Deck in Minutes</h2>
            <p>Three steps — upload, analyse, export. That’s the whole thing.</p>
          </div>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Upload Data</h3>
              <p>
                SPSS (<code>.sav</code>), Excel, or survey platform exports. Metadata, labels,
                and multi-response sets are detected automatically.
              </p>
              <div style={{ marginTop: '20px' }}>
                <MediaPlaceholder
                  type="image"
                  src="/media/step1-upload.png"
                  suggestedFilename="step1-upload.png"
                  suggestedSize="1100×688"
                  description="Upload screen with .sav file + datamap auto-detection"
                  aspectRatio="16/10"
                />
              </div>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Auto Analysis</h3>
              <p>
                Crosstabs with banners · significance testing · weighting applied automatically.
                AI drafts the tab plan from your questionnaire; the engine runs it.
              </p>
              <div style={{ marginTop: '20px' }}>
                <MediaPlaceholder
                  type="image"
                  src="/media/step2-analysis.png"
                  suggestedFilename="step2-analysis.png"
                  suggestedSize="1100×688"
                  description="Crosstab view with banner + AI tab-plan panel"
                  aspectRatio="16/10"
                />
              </div>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Generate Reports</h3>
              <p>
                Charts + tables + AI-assisted summaries that cite real engine numbers.
                Export to PowerPoint, Excel, or PDF in one click.
              </p>
              <div style={{ marginTop: '20px' }}>
                <MediaPlaceholder
                  type="image"
                  src="/media/step3-export.png"
                  suggestedFilename="step3-export.png"
                  suggestedSize="1100×688"
                  description="Excel tab book + PowerPoint deck export preview"
                  aspectRatio="16/10"
                />
              </div>
            </div>
          </div>
          <p style={{ textAlign: 'center', marginTop: '32px', color: '#64748b', fontSize: '15px', fontStyle: 'italic' }}>
            AI is a co-pilot, not a replacement. Researchers stay in control; the system removes the 70–80% that was manual.
          </p>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="who-its-for-section" style={{ padding: '80px 0', background: '#ffffff' }}>
        <div className="section-container">
          <div className="section-header">
            <div className="section-subtitle">Built for Research Professionals</div>
            <h2>Who It’s For</h2>
            <p>Designed for the people who actually produce the tables — not generic BI users</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', maxWidth: '1100px', margin: '0 auto' }}>
            {[
              {
                icon: '🏢',
                title: 'Market Research Agencies',
                desc: 'Turn around tab books faster, keep DP costs flat, spend more time on the story.'
              },
              {
                icon: '⚙️',
                title: 'Data Processing Teams',
                desc: 'One project definition for datamap, banners, weights, MRSETs. Reuse across every wave.'
              },
              {
                icon: '📈',
                title: 'In-House Insights Teams',
                desc: 'Self-serve ad-hoc cuts. Natural-language queries trigger real analyses, not AI guesses.'
              },
              {
                icon: '🌐',
                title: 'Panel Providers',
                desc: 'Offer value-added tabulation alongside sample. White-label exports, API-driven imports.'
              }
            ].map((item, i) => (
              <div key={i} style={{
                padding: '28px 24px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                borderRadius: '14px',
                border: '1px solid #e2e8f0',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>{item.icon}</div>
                <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>{item.title}</h3>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real Impact Section */}
      <section className="real-impact-section" style={{
        padding: '80px 0',
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
        color: 'white'
      }}>
        <div className="section-container" style={{ textAlign: 'center' }}>
          <div className="section-subtitle" style={{ color: '#c7d2fe', fontSize: '13px', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 600, marginBottom: '12px' }}>Real Impact</div>
          <h2 style={{ color: 'white', fontSize: '32px', marginBottom: '20px' }}>From Days to Hours on a Typical Tracker</h2>
          <p style={{ color: '#c7d2fe', fontSize: '17px', maxWidth: '720px', margin: '0 auto 48px', lineHeight: 1.7 }}>
            A study that takes <strong style={{ color: '#fbbf24' }}>6–8 hours</strong> to analyze and
            report manually — can be completed in <strong style={{ color: '#10b981' }}>under an hour</strong> with KrossIQ,
            once banners and templates are set up.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '24px', maxWidth: '900px', margin: '0 auto' }}>
            {[
              { metric: 'Tracker wave', before: '3 days', after: '4 hours' },
              { metric: 'Ad-hoc U&A', before: '1 week', after: '1 day' },
              { metric: 'NPS read-out', before: '2 hours', after: '5 minutes' }
            ].map((item, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.08)',
                padding: '24px',
                borderRadius: '12px',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.12)'
              }}>
                <div style={{ fontSize: '13px', color: '#c7d2fe', marginBottom: '10px', fontWeight: 600 }}>{item.metric}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '18px', color: '#fca5a5', textDecoration: 'line-through' }}>{item.before}</span>
                  <span style={{ fontSize: '20px', color: 'rgba(255,255,255,0.5)' }}>→</span>
                  <span style={{ fontSize: '22px', color: '#10b981', fontWeight: 700 }}>{item.after}</span>
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: '13px', color: '#c7d2fe', marginTop: '32px', fontStyle: 'italic', opacity: 0.8 }}>
            Based on early-pilot feedback. Actual savings vary with study complexity and template maturity.
          </p>

          <div style={{ marginTop: '48px', maxWidth: '900px', marginLeft: 'auto', marginRight: 'auto' }}>
            <MediaPlaceholder
              type="image"
              src="/media/impact-before-after.png"
              suggestedFilename="impact-before-after.png"
              suggestedSize="1200×675"
              description="Before/after — manual SPSS workflow vs KrossIQ tab book in one screen"
              aspectRatio="16/9"
            />
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="download-section">
        <div className="section-container">
          <div className="download-content">
            <div className="download-info">
              <div className="section-subtitle">Get Started Today</div>
              <h2>Ready to Supercharge Your Data Analysis?</h2>
              <p>Download KrossIQ and start creating professional tables in minutes. No complex setup required.</p>
              <ul className="download-features">
                {[
                  '14-day free trial included',
                  'Full feature access during trial',
                  'No credit card required',
                  'Works offline — your data stays local',
                  'Priority support during trial',
                  'Regular updates included'
                ].map((feature, index) => (
                  <li key={index}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="download-card">
              <div className="download-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              </div>
              <h3>KrossIQ for Windows</h3>
              <p className="version">Version {release.version || '...'} | Windows 10/11</p>
              {error ? (
                <p className="download-error" style={{ color: '#ef4444', marginBottom: '1rem' }}>
                  {error}. Please refresh or contact support.
                </p>
              ) : null}
              <a
                href={release.file_url || '#'}
                className={`btn btn-primary btn-lg download-btn ${!release.file_url ? 'disabled' : ''}`}
                download={release.file_name}
                onClick={(e) => {
                  if (!release.file_url) {
                    e.preventDefault();
                    if (!loading) {
                      alert('Download not available. Please try refreshing the page.');
                    }
                  } else {
                    // Track download — send notification email
                    fetch('/api/licenses/public/track-download/', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ version: release.version, file_name: release.file_name }),
                    }).catch(() => {});
                  }
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {loading ? 'Loading...' : 'Download Now (Free Trial)'}
              </a>
              <p className="download-size">{formatFileSize(release.file_size)} | Requires Windows 10 or later</p>
              <div className="download-notes">
                <span className="note">✓ No installation restrictions</span>
                <span className="note">✓ Cancel anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="section-container">
          <div className="section-header">
            <div className="section-subtitle">Choose Your Plan</div>
            <h2>Simple, Transparent Pricing</h2>
            <p>Select the plan that fits your research needs</p>
          </div>
          <div className="pricing-grid">
            <div className="pricing-card featured">
              <div className="featured-badge">Most Popular</div>
              <h3>Professional</h3>
              <div className="price">
                <span className="original-price">$2,000</span>
                <span className="amount">$1,499</span>
                <span className="period">per year</span>
                <span className="discount-badge">25% OFF</span>
              </div>
              <ul className="pricing-features">
                {[
                  'Unlimited SPSS file imports',
                  'Cross-tabulation with significance testing',
                  'Data append, replace & stacking',
                  'Factor Analysis & Correlation',
                  'TURF & Driver Analysis',
                  'Data QC & Validation tools',
                  'AI-Powered Assistance',
                  'Export to Excel',
                  'Data weighting support',
                  'Regular updates included'
                ].map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
              <a href="#download" className="btn btn-primary btn-lg">Start 14-Day Free Trial</a>
              <p className="pricing-note">Try all features free. No credit card required.</p>
            </div>
            <div className="pricing-card">
              <h3>Enterprise</h3>
              <div className="price">
                <span className="amount">Custom</span>
                <span className="period">contact for pricing</span>
              </div>
              <ul className="pricing-features">
                {[
                  'Everything in Professional',
                  'Volume licensing discounts',
                  'Priority support',
                  'Custom integrations',
                  'Dedicated account manager',
                  'On-premise deployment options',
                  'Custom training sessions',
                  'SLA guarantees'
                ].map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
              <button onClick={handleBookDemo} className="btn btn-primary btn-lg">Book a Demo</button>
              <button onClick={handleContactSales} className="btn btn-outline btn-lg" style={{ marginTop: '0.5rem' }}>Contact Sales</button>
              <p className="pricing-note">Get a customized quote for your team.</p>
            </div>
          </div>
          {/* AI Credits Section */}
          <div className="byok-section">
            <div className="byok-header">
              <div className="byok-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>
              <h3>AI Credits Included</h3>
              <p className="byok-subtitle">Simple, Transparent AI Pricing</p>
            </div>
            <div className="byok-content">
              <p className="byok-description">
                Every license includes <strong>free AI credits</strong> to get you started. Use credits for AI-powered
                summaries, data computation assistance, and intelligent suggestions. <strong>No API keys needed</strong> — it just works.
              </p>
              <div className="byok-benefits">
                <div className="byok-benefit">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <div>
                    <strong>5000 Trial Credits + 250/month Free</strong>
                    <span>14-day trial includes 5000 credits. After trial, 250 free credits every month</span>
                  </div>
                </div>
                <div className="byok-benefit">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <div>
                    <strong>Simple Credit Pricing</strong>
                    <span>Additional credits at just $0.01 each</span>
                  </div>
                </div>
                <div className="byok-benefit">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <div>
                    <strong>Credits Never Expire</strong>
                    <span>One-time credits — use at your own pace</span>
                  </div>
                </div>
                <div className="byok-benefit">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <div>
                    <strong>Bulk Discounts Available</strong>
                    <span>Save up to 20% on larger credit packages</span>
                  </div>
                </div>
              </div>
              <p className="byok-note">
                Unlike competitors who charge per AI query or bundle hidden AI costs into premium tiers,
                our credit system gives you full transparency and control over your AI usage.
              </p>
              <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                <a href="/buy-credits" className="btn btn-primary" style={{ display: 'inline-block' }}>Buy AI Credits</a>
              </div>
            </div>
          </div>

          <div className="contact-admin-section">
            <div className="contact-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <h3>Need Help or Have Questions?</h3>
            <p>Our team is here to help you get the most out of KrossIQ.</p>
            <a href="mailto:support@krossiq.com" className="btn btn-secondary">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              support@krossiq.com
            </a>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="comparison-section">
        <div className="section-container">
          <div className="section-header">
            <div className="section-subtitle">Why Not Traditional Tools?</div>
            <h2>What Your Current Stack Leaves Undone</h2>
            <p>KrossIQ consolidates the workflow — here’s the capability view</p>
          </div>

          {/* Quick-read summary — the 4-row pitch */}
          <div style={{
            maxWidth: '820px', margin: '0 auto 40px',
            background: 'white', border: '1px solid #e2e8f0',
            borderRadius: '14px', overflow: 'hidden',
            boxShadow: '0 4px 16px rgba(0,0,0,0.04)'
          }}>
            <div style={{
              padding: '16px 20px',
              background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
              fontWeight: 700, fontSize: '15px', color: '#1e293b',
              display: 'grid', gridTemplateColumns: '1fr 2fr',
              gap: '16px'
            }}>
              <span>Tool</span>
              <span>What’s still manual</span>
            </div>
            {[
              { tool: 'SPSS', pain: 'Powerful syntax, but reporting is manual. No integrated AI assistance.' },
              { tool: 'Q Research Software', pain: 'Strong tabulation, requires expertise, reporting still manual.' },
              { tool: 'Displayr', pain: 'Feature-rich, complex learning curve, not workflow-focused.' },
              { tool: 'KrossIQ', pain: 'End-to-end: data → tables → AI-assisted deck. One workflow.', highlight: true }
            ].map((row, i) => (
              <div key={i} style={{
                padding: '14px 20px',
                display: 'grid', gridTemplateColumns: '1fr 2fr',
                gap: '16px', alignItems: 'center',
                borderTop: '1px solid #f1f5f9',
                background: row.highlight ? 'linear-gradient(90deg, #ecfdf5 0%, #f0fdf4 100%)' : 'white'
              }}>
                <span style={{
                  fontWeight: row.highlight ? 700 : 600,
                  color: row.highlight ? '#059669' : '#1e293b',
                  fontSize: '14px'
                }}>{row.tool}{row.highlight && ' ✓'}</span>
                <span style={{ fontSize: '14px', color: '#475569' }}>{row.pain}</span>
              </div>
            ))}
          </div>
          {/* Desktop: Full comparison table */}
          <div className="comparison-table comparison-desktop">
            {[
              ['Feature', 'KrossIQ', 'SPSS', 'Q Research', 'Excel'],
              ['Cross-tabulation', 'check', 'partial', 'check', 'partial'],
              ['Significance testing', 'check', 'check', 'check', 'cross'],
              ['AI Assistance', 'check', 'cross', 'partial', 'cross'],
              ['AI Syntax Generation', 'check', 'cross', 'cross', 'cross'],
              ['AI Table Generation (< 5 min)', 'check', 'cross', 'cross', 'cross'],
              ['CBC Conjoint Analysis', 'check', 'cross', 'partial', 'cross'],
              ['MaxDiff Analysis', 'check', 'cross', 'partial', 'cross'],
              ['Excel-style Data Filtering', 'check', 'cross', 'partial', 'check'],
              ['Data Operations', 'check', 'check', 'check', 'cross'],
              ['Graceful Expiry (keep your data)', 'check', 'cross', 'cross', 'cross'],
              ['Learning curve', 'check', 'cross', 'cross', 'partial'],
              ['Export to Excel', 'check', 'partial', 'check', 'check'],
              ['Data Visualization', 'check', 'check', 'check', 'partial'],
              ['Price (Annual)', '$1,499', '$1,290+', '$3,000+', '$99+']
            ].map((row, index) => (
              <div key={index} className={`comparison-row ${index === 0 ? 'header' : ''}`}>
                {row.map((cell, cellIndex) => (
                  <span key={cellIndex} className={getCellClass(cell)}>
                    {cell === 'check' ? '✓' : cell === 'partial' ? '△' : cell === 'cross' ? '✗' : cell}
                  </span>
                ))}
              </div>
            ))}
          </div>

          {/* Mobile: Feature comparison cards with icons per tool */}
          <div className="comparison-mobile">
            {[
              { feature: 'Cross-tabulation', k: '✓', s: '△', q: '✓', e: '△' },
              { feature: 'Significance Testing', k: '✓', s: '✓', q: '✓', e: '✗' },
              { feature: 'AI Assistance', k: '✓', s: '✗', q: '△', e: '✗' },
              { feature: 'AI Syntax Generation', k: '✓', s: '✗', q: '✗', e: '✗' },
              { feature: 'AI Table Generation', k: '✓', s: '✗', q: '✗', e: '✗' },
              { feature: 'CBC Conjoint', k: '✓', s: '✗', q: '△', e: '✗' },
              { feature: 'MaxDiff Analysis', k: '✓', s: '✗', q: '△', e: '✗' },
              { feature: 'Excel-style Filtering', k: '✓', s: '✗', q: '△', e: '✓' },
              { feature: 'Data Operations', k: '✓', s: '✓', q: '✓', e: '✗' },
              { feature: 'Graceful Expiry', k: '✓', s: '✗', q: '✗', e: '✗' },
              { feature: 'Easy Learning Curve', k: '✓', s: '✗', q: '✗', e: '△' },
              { feature: 'Export to Excel', k: '✓', s: '△', q: '✓', e: '✓' },
              { feature: 'Data Visualization', k: '✓', s: '✓', q: '✓', e: '△' },
            ].map((item, index) => (
              <div key={index} className="comparison-card">
                <div className="comparison-card-feature">{item.feature}</div>
                <div className="comparison-card-tools">
                  <span className={`tool-badge ${item.k === '✓' ? 'yes' : item.k === '△' ? 'partial' : 'no'}`}>
                    <strong>KrossIQ</strong> {item.k}
                  </span>
                  <span className={`tool-badge ${item.s === '✓' ? 'yes' : item.s === '△' ? 'partial' : 'no'}`}>
                    SPSS {item.s}
                  </span>
                  <span className={`tool-badge ${item.q === '✓' ? 'yes' : item.q === '△' ? 'partial' : 'no'}`}>
                    Q {item.q}
                  </span>
                  <span className={`tool-badge ${item.e === '✓' ? 'yes' : item.e === '△' ? 'partial' : 'no'}`}>
                    Excel {item.e}
                  </span>
                </div>
              </div>
            ))}
            <div className="comparison-price-card">
              <div className="comparison-price-row">
                <span className="price-tool highlight">KrossIQ <strong>$1,499</strong></span>
                <span className="price-tool">SPSS <strong>$1,290+</strong></span>
                <span className="price-tool">Q Research <strong>$3,000+</strong></span>
                <span className="price-tool">Excel <strong>$99+</strong></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section style={{
        padding: '72px 0',
        background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #8b5cf6 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div className="section-container" style={{ maxWidth: '820px' }}>
          <h2 style={{ color: 'white', fontSize: '34px', fontWeight: 800, marginBottom: '16px', lineHeight: 1.2 }}>
            Ready to transform your research workflow?
          </h2>
          <p style={{ color: '#e0e7ff', fontSize: '17px', marginBottom: '32px', lineHeight: 1.6 }}>
            See a 10-minute walkthrough with your own study type, or start the free trial today.
            No credit card required.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={handleBookDemo}
              style={{
                padding: '14px 28px',
                background: 'white',
                color: '#4f46e5',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
            >
              Book a 10-min Demo
            </button>
            <a
              href="#download"
              style={{
                padding: '14px 28px',
                background: 'rgba(255,255,255,0.1)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.35)',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: 700,
                textDecoration: 'none',
                display: 'inline-block',
                backdropFilter: 'blur(10px)'
              }}
            >
              Try Free for 14 Days
            </a>
          </div>
          <p style={{ color: '#c7d2fe', fontSize: '13px', marginTop: '20px', opacity: 0.8 }}>
            Your data stays yours. Even if your trial expires, existing projects remain fully accessible.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-brand">
            <a href="#top" className="nav-logo" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
              <img src={logo} alt="KrossIQ" className="logo-image" />
            </a>
            <p>Powerful data tabulation & analysis for market research professionals.</p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#how-it-works">How It Works</a>
              <a href="#pricing">Pricing</a>
              <a href="#download">Download</a>
            </div>
            <div className="footer-column">
              <h4>Resources</h4>
              <Link to="/blog">Blog</Link>
            </div>
            <div className="footer-column">
              <h4>Support</h4>
              <a href="mailto:support@krossiq.com">Email Support</a>
            </div>
            <div className="footer-column">
              <h4>Legal</h4>
              <Link to="/terms">Terms & Conditions</Link>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} KrossIQ. All rights reserved.</p>
        </div>
      </footer>

      {/* Contact Form Widget */}
      <ContactForm />

      {/* WhatsApp Chat Button */}
      <a
        href="https://wa.me/919384390750?text=Hi%2C%20I%27m%20interested%20in%20KrossIQ"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: 'fixed',
          bottom: '90px',
          right: '24px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          backgroundColor: '#25D366',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(37, 211, 102, 0.4)',
          zIndex: 9999,
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 211, 102, 0.5)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 211, 102, 0.4)'; }}
        title="Chat with us on WhatsApp"
      >
        <svg viewBox="0 0 32 32" width="28" height="28" fill="white">
          <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16.004c0 3.502 1.14 6.742 3.072 9.372L1.062 31.1l5.898-1.95c2.516 1.736 5.572 2.754 8.862 2.754h.008C24.658 31.904 32 24.726 32 16.004 32 7.282 24.658 0 16.004 0zm9.53 22.61c-.396 1.116-2.322 2.132-3.246 2.27-.924.138-2.112.198-3.408-.216a31.04 31.04 0 01-3.084-1.14c-5.43-2.346-8.976-7.83-9.246-8.196-.27-.366-2.208-2.94-2.208-5.61 0-2.67 1.398-3.984 1.896-4.53.498-.546 1.086-.684 1.446-.684.36 0 .72.004 1.038.018.33.018.78-.126 1.218.93.444 1.068 1.506 3.678 1.638 3.942.132.264.222.576.042.93-.18.354-.27.576-.534.888-.264.312-.558.696-.792.936-.264.264-.54.552-.234 1.086.306.534 1.362 2.25 2.922 3.648 2.004 1.794 3.696 2.352 4.218 2.616.528.264.834.222 1.14-.138.306-.354 1.314-1.536 1.662-2.064.348-.528.696-.444 1.176-.264.48.18 3.048 1.44 3.57 1.698.522.264.87.396 1.002.612.132.216.132 1.254-.264 2.37z"/>
        </svg>
      </a>
    </div>
  );
};

// Helper function for feature icons
const getFeatureIcon = (iconName) => {
  const icons = {
    table: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></svg>,
    database: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></svg>,
    brain: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>,
    chart: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18" /><path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" /></svg>,
    box: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="7.5 4.21 12 6.81 16.5 4.21" /><polyline points="7.5 19.79 7.5 14.6 3 12" /><polyline points="21 12 16.5 14.6 16.5 19.79" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>,
    target: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>,
    trend: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>,
    export: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>,
    scale: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>,
    check: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4" /><path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c2.12 0 4.07.74 5.62 1.98" /></svg>,
    'box-stats': <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>,
    correlation: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></svg>,
    maxdiff: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 20V10" /><path d="M12 20V4" /><path d="M6 20v-6" /><circle cx="18" cy="7" r="2" /><circle cx="6" cy="11" r="2" /></svg>,
    shield: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></svg>,
    conjoint: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="8" height="6" rx="1" /><rect x="14" y="3" width="8" height="6" rx="1" /><rect x="2" y="15" width="8" height="6" rx="1" /><rect x="14" y="15" width="8" height="6" rx="1" /><path d="M6 9v6" /><path d="M18 9v6" /><path d="M10 6h4" /><path d="M10 18h4" /></svg>,
    sig: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 20h16" /><path d="M8 16V8" /><path d="M16 16V4" /><path d="M12 16v-6" /><circle cx="8" cy="6" r="2" /><circle cx="16" cy="2" r="2" /><path d="M5 13l14-6" strokeDasharray="2 2" /></svg>
  };
  return icons[iconName] || icons.table;
};

// Helper function for comparison table cell classes
const getCellClass = (cell) => {
  if (cell === 'check') return 'check';
  if (cell === 'partial') return 'partial';
  if (cell === 'cross') return 'cross';
  return '';
};

export default LandingPage;

